import { ChangeDetectionStrategy,Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { languages } from '@codemirror/language-data';
import { forkJoin } from 'rxjs';

import { AdminComponentBase, PageSimpleContentComponent, PageStatus } from 'app/layout/content';
import { AssetCustomActionEvent, AssetsEditorComponent, EditorFileInformation, UploadAssetEditor } from 'app/shared/components/assets-editor.component';
import { ErrorComponent } from 'app/shared/components/error.component';

import { StoreDesignerAssetsService } from './store-designer-assets.service';

export type StoreAssetEditorInformation = EditorFileInformation & {
    contentHash?: string;
    publicUrl: string;
}

@Component({
    selector: 'admin-store-designer-assets',
    imports: [
        PageSimpleContentComponent,
        ErrorComponent,
        AssetsEditorComponent,
    ],
    templateUrl: './store-designer-assets.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './store-designer-assets.component.scss',
})
export class StoreDesignerAssetsComponent extends AdminComponentBase implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    public readonly designerAssetsService = inject(StoreDesignerAssetsService);
    private readonly snackBar = inject(MatSnackBar);

    @ViewChild('assetEditor', { static: false }) assetEditor: AssetsEditorComponent<StoreAssetEditorInformation>;

    storeId: string;
    assets: StoreAssetEditorInformation[] = [];

    ngOnDestroy(): void {
        super.unsubscribeSubscriptions();
    }

    ngOnInit(): void {
        this.registerSubscription(this.route.params.subscribe(params => {
            this.storeId = params.storeId;
            this.loadAssets();
        }));
    }

    loadAssets() {
        this.pageStatus = PageStatus.loading;
        this.subscribeWithGenericLoadingErrorHandling(this.designerAssetsService.listAssets(this.storeId), assets => {
            this.assets = assets;
            this.pageStatus = PageStatus.loaded;
        });
    }

    protected readonly languages = languages;

    protected uploadAsset(asset: UploadAssetEditor) {
        let contentBase64 = 'content' in asset
            ? btoa(asset.content)
            : asset.contentBase64
        ;

        this.subscribeWithGenericSavingErrorHandling(this.designerAssetsService.uploadAssetEditorFile({
            filename: asset.filename,
            contentBase64: contentBase64,
            storeId: this.storeId,
        }), (result) => {
            this.updateAssetAfterUpload({
                ...result,
                contentHash: result.contentHash || undefined,
            });
        });
    }

    protected uploadAssets(assets: UploadAssetEditor[]) {
        // FIXME: Instead of doing multiple calls, consider batching uploads to improve performance, in request with multiple mutations
        this.subscribeWithGenericSavingErrorHandling(forkJoin(
            assets.map(asset => this.designerAssetsService.uploadAssetEditorFile({
                filename: asset.filename,
                contentBase64: 'content' in asset
                    ? btoa(asset.content)
                    : asset.contentBase64,
                storeId: this.storeId,
            })),
        ), (results) => {
            for (let result of results) {
                this.updateAssetAfterUpload({
                    ...result,
                    contentHash: result.contentHash || undefined,
                });
            }
        });
    }

    private updateAssetAfterUpload(updatedAssetEditorInfo: StoreAssetEditorInformation) {
        let assetIndex = this.assets.findIndex(x => x.filename == updatedAssetEditorInfo.filename);
        if (assetIndex != -1) {
            this.assets[assetIndex] = updatedAssetEditorInfo;
        } else {
            this.assets.push(updatedAssetEditorInfo);
        }
        this.assetEditor.updateFile(updatedAssetEditorInfo);
    }

    protected deleteAsset(asset: EditorFileInformation) {
        this.subscribeWithGenericSavingErrorHandling(this.designerAssetsService.deleteAssetEditorFile({
            filename: asset.filename,
            storeId: this.storeId,
        }), (_) => {
            let assetIndex = this.assets.findIndex(x => x.filename == asset.filename);
            if (assetIndex != -1) {
                this.assets.splice(assetIndex, 1);
            }
        });
    }

    protected handleEditorCustomAction(event: AssetCustomActionEvent<StoreAssetEditorInformation>) {
        if (event.actionName === 'copy_designer_relative_path') {
            this.copyDesignerUrl(event.file);
        } else if (event.actionName === 'copy_css_relative_path') {
            this.copyCssUrl(event.file);
        } else if (event.actionName === 'copy_full_path') {
            this.copyFullUrl(event.file);
        }
    }

    private copyDesignerUrl(file: StoreAssetEditorInformation) {
        if (!file) {
            return;
        }

        let url = `/stores/${this.storeId}/assets/${file.filename}`;
        if (file.contentHash) {
            url += `?hash=${file.contentHash}`;
        }
        navigator.clipboard.writeText(url)
            .then(() => {
                this.snackBar.open('Designer path copied to clipboard', undefined, { duration: 2000 });
            })
            .catch(_err => {
                this.snackBar.open('Failed to copy to clipboard', undefined, { duration: 2000 });
            });
    }

    private copyCssUrl(file: StoreAssetEditorInformation) {
        if (!file) {
            return;
        }
        let url = `./assets/${file.filename}`;
        if (file.contentHash) {
            url += `?hash=${file.contentHash}`;
        }
        navigator.clipboard.writeText(url)
            .then(() => {
                this.snackBar.open('CSS path copied to clipboard', undefined, { duration: 2000 });
            })
            .catch(_err => {
                this.snackBar.open('Failed to copy to clipboard', undefined, { duration: 2000 });
            });
    }

    private copyFullUrl(file: StoreAssetEditorInformation) {
        if (!file) {
            return;
        }

        let url = new URL(file.publicUrl);
        if (file.contentHash) {
            url.searchParams.set('hash', file.contentHash);
        }

        navigator.clipboard.writeText(url.toString())
            .then(() => {
                this.snackBar.open('CSS path copied to clipboard', undefined, { duration: 2000 });
            })
            .catch(_err => {
                this.snackBar.open('Failed to copy to clipboard', undefined, { duration: 2000 });
            });
    }
}
